import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

const galleryImages = [
  {
    src: "https://images.pexels.com/photos/33404981/pexels-photo-33404981.jpeg?auto=compress&w=800",
    alt: "Roof replacement in progress",
    span: "md:col-span-8 md:row-span-2",
    height: "h-64 md:h-full",
    label: "Roofing",
  },
  {
    src: "https://images.unsplash.com/photo-1770044001869-12315d4d6884?w=800&auto=format",
    alt: "Completed shingled roof",
    span: "md:col-span-4",
    height: "h-64",
    label: "Roofing",
  },
  {
    src: "https://images.pexels.com/photos/16690918/pexels-photo-16690918.jpeg?auto=compress&w=800",
    alt: "House painting before and after",
    span: "md:col-span-4",
    height: "h-64",
    label: "Painting",
  },
  {
    src: "https://images.unsplash.com/photo-1646009653015-602d963494bc?w=800&auto=format",
    alt: "Freshly painted home exterior",
    span: "md:col-span-4",
    height: "h-64 md:h-80",
    label: "Painting",
  },
  {
    src: "https://images.unsplash.com/photo-1771968965646-9818a67c186a?w=800&auto=format",
    alt: "Modern door installation",
    span: "md:col-span-4",
    height: "h-64 md:h-80",
    label: "Windows & Doors",
  },
  {
    src: "https://images.unsplash.com/photo-1775359924001-9cf0e5d3adf5?w=800&auto=format",
    alt: "Large modern windows installed",
    span: "md:col-span-4",
    height: "h-64 md:h-80",
    label: "Windows & Doors",
  },
  {
    src: "https://images.unsplash.com/photo-1723470916372-325154109b74?w=800&auto=format",
    alt: "Beautiful kitchen renovation",
    span: "md:col-span-6",
    height: "h-64 md:h-72",
    label: "Home Addition",
  },
  {
    src: "https://images.unsplash.com/photo-1632143697739-5559b57ac985?w=800&auto=format",
    alt: "Home addition under construction",
    span: "md:col-span-6",
    height: "h-64 md:h-72",
    label: "Home Addition",
  },
  {
    src: "https://images.unsplash.com/photo-1632624759923-86919326bd0f?w=800&auto=format",
    alt: "Fire restoration work",
    span: "md:col-span-4",
    height: "h-64",
    label: "Restoration",
  },
  {
    src: "https://images.pexels.com/photos/12119354/pexels-photo-12119354.jpeg?auto=compress&w=800",
    alt: "Bathroom renovation complete",
    span: "md:col-span-4",
    height: "h-64",
    label: "Restoration",
  },
  {
    src: "https://images.unsplash.com/photo-1765959617888-30837a158667?w=800&auto=format",
    alt: "Finished bonus room",
    span: "md:col-span-4",
    height: "h-64",
    label: "Bonus Room",
  },
  {
    src: "https://images.unsplash.com/photo-1628744448839-170bf324f27e?w=800&auto=format",
    alt: "Beautiful home with new driveway",
    span: "md:col-span-8",
    height: "h-64 md:h-80",
    label: "Driveway",
  },
  {
    src: "https://images.pexels.com/photos/3935333/pexels-photo-3935333.jpeg?auto=compress&w=800",
    alt: "Completed driveway project",
    span: "md:col-span-4",
    height: "h-64 md:h-80",
    label: "Driveway",
  },
  {
    src: "https://images.unsplash.com/photo-1748908271592-d9d5690b288b?w=800&auto=format",
    alt: "New wood fence at sunset",
    span: "md:col-span-6",
    height: "h-64 md:h-72",
    label: "Fencing",
  },
  {
    src: "https://images.unsplash.com/photo-1740482682683-309e6fb4898f?w=800&auto=format",
    alt: "Privacy fence installation",
    span: "md:col-span-6",
    height: "h-64 md:h-72",
    label: "Fencing",
  },
];

export default function GalleryPage() {
  return (
    <div data-testid="gallery-page">
      {/* Header */}
      <section className="bg-brand-dark py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-3">
              Our Work
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white">
              Project Gallery
            </h1>
            <p className="font-dm-sans text-neutral-400 text-base mt-4 max-w-lg">
              Browse through our completed projects and see the quality
              craftsmanship we bring to every job.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            {galleryImages.map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className={`gallery-item overflow-hidden relative group ${img.span}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className={`w-full ${img.height} object-cover`}
                  loading="lazy"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="font-outfit font-bold text-white text-sm uppercase tracking-wider">{img.label}</span>
                  <p className="font-dm-sans text-white/80 text-xs mt-1">{img.alt}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight uppercase text-brand-dark mb-4">
            Like What You See?
          </h2>
          <p className="font-dm-sans text-neutral-500 mb-8 max-w-md mx-auto">
            Let us bring the same level of quality to your next project.
          </p>
          <Link to="/contact">
            <Button className="bg-brand text-white font-outfit font-bold uppercase tracking-wider text-sm px-10 py-6 hover:bg-brand-hover rounded-none">
              Start Your Project
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
